import { DEFAULT_WEAPON_MODS, type LoadoutMods, type WeaponKey, type WeaponMods } from "./loadout";
import { mulberry32, pickWeighted } from "./rng";

// Field mods are the in-run upgrade currency of an Operation: after every
// mission win the player drafts 1 of 3. They stack multiplicatively, are
// scoped to the run, and die with it — build variety comes from the draft,
// not from a permanent shop. The Armory (unlocks.ts) widens this pool
// between runs; it never grants raw stats.

export type Rarity = "common" | "rare" | "epic";

export type FieldModEffects = {
  weapon?: WeaponKey | "all";
  cooldownMult?: number;
  spreadMult?: number;
  blastMult?: number;
  tracerBright?: boolean;
  // Squad-side effects
  squadDamageTakenMult?: number; // < 1 = plates
  squadBonusMembers?: number;
  // Aircraft-side effects
  aircraftDamageTakenMult?: number; // < 1 = hardened airframe
  bonusFlares?: number;
  // Mission-side effects
  reinforcementBudgetDelta?: number; // < 0 = interdiction, fewer ambient spawns
};

export type FieldMod = {
  id: string;
  name: string;
  desc: string;
  rarity: Rarity;
  effects: FieldModEffects;
};

const RARITY_WEIGHT: Record<Rarity, number> = { common: 6, rare: 3, epic: 1 };

// The base pool every profile starts with. Armory unlocks append to this.
export const BASE_FIELD_MODS: FieldMod[] = [
  // Commons — solid single-weapon bumps
  { id: "fm-cg-rate", name: "QUICK SPIN", desc: "Chaingun +20% fire rate", rarity: "common", effects: { weapon: "chaingun", cooldownMult: 0.8 } },
  { id: "fm-cg-bore", name: "TIGHT BORE", desc: "Chaingun 25% tighter spread", rarity: "common", effects: { weapon: "chaingun", spreadMult: 0.75 } },
  { id: "fm-ac-load", name: "FAST LOAD", desc: "Autocannon +25% reload speed", rarity: "common", effects: { weapon: "autocannon", cooldownMult: 0.75 } },
  { id: "fm-ac-frag", name: "FRAG SHELLS", desc: "Autocannon +30% blast radius", rarity: "common", effects: { weapon: "autocannon", blastMult: 1.3 } },
  { id: "fm-how-crew", name: "FAST CREW", desc: "Howitzer -25% reload time", rarity: "common", effects: { weapon: "howitzer", cooldownMult: 0.75 } },
  { id: "fm-how-fuze", name: "PROX FUZE", desc: "Howitzer +25% blast radius", rarity: "common", effects: { weapon: "howitzer", blastMult: 1.25 } },
  { id: "fm-flares", name: "FLARE RACKS", desc: "+2 flare charges per mission", rarity: "common", effects: { bonusFlares: 2 } },
  // Rares — squad and mission shape
  { id: "fm-plates", name: "CERAMIC PLATES", desc: "Squad takes 25% less damage", rarity: "rare", effects: { squadDamageTakenMult: 0.75 } },
  { id: "fm-airframe", name: "HARDENED AIRFRAME", desc: "Specter takes 25% less damage", rarity: "rare", effects: { aircraftDamageTakenMult: 0.75 } },
  { id: "fm-interdict", name: "ROUTE INTERDICTION", desc: "Enemy reinforcements -4 per mission", rarity: "rare", effects: { reinforcementBudgetDelta: -4 } },
  { id: "fm-gun-crew", name: "VETERAN CREW", desc: "All weapons +12% fire rate", rarity: "rare", effects: { weapon: "all", cooldownMult: 0.88 } },
  { id: "fm-ac-over", name: "OVERPRESSURE", desc: "Autocannon +45% blast radius", rarity: "rare", effects: { weapon: "autocannon", blastMult: 1.45 } },
  // Epics — run-defining
  { id: "fm-attach", name: "ATTACHMENT", desc: "+1 squad member (and +1 max)", rarity: "epic", effects: { squadBonusMembers: 1 } },
  { id: "fm-how-shatter", name: "SHATTER SHELLS", desc: "Howitzer +60% blast radius", rarity: "epic", effects: { weapon: "howitzer", blastMult: 1.6 } },
];

// Armory-unlockable mods (referenced by unlocks.ts) — not in the base pool.
export const UNLOCKABLE_FIELD_MODS: FieldMod[] = [
  { id: "fm-cg-tracer", name: "BRIGHT TRACE", desc: "Chaingun hi-vis tracers + 10% tighter spread", rarity: "common", effects: { weapon: "chaingun", tracerBright: true, spreadMult: 0.9 } },
  { id: "fm-cg-storm", name: "STORM BARREL", desc: "Chaingun +35% fire rate", rarity: "rare", effects: { weapon: "chaingun", cooldownMult: 0.65 } },
  { id: "fm-hard-plates", name: "HARDENED PLATES", desc: "Squad takes 40% less damage", rarity: "epic", effects: { squadDamageTakenMult: 0.6 } },
  { id: "fm-blockade", name: "AIR BLOCKADE", desc: "Enemy reinforcements -8 per mission", rarity: "epic", effects: { reinforcementBudgetDelta: -8 } },
  { id: "fm-ac-auto", name: "AUTOLOADER", desc: "Autocannon +40% reload speed", rarity: "epic", effects: { weapon: "autocannon", cooldownMult: 0.6 } },
  { id: "fm-how-battery", name: "TWIN BATTERY", desc: "Howitzer -40% reload time", rarity: "epic", effects: { weapon: "howitzer", cooldownMult: 0.6 } },
];

const ALL_FIELD_MODS = [...BASE_FIELD_MODS, ...UNLOCKABLE_FIELD_MODS];

export function fieldModById(id: string): FieldMod | undefined {
  return ALL_FIELD_MODS.find((m) => m.id === id);
}

// Draft 3 distinct choices from the pool, rarity-weighted, deterministic in
// (seed, draftIndex). Already-drafted mods are excluded — every mod is
// draft-once so stacking comes from drafting *different* mods.
export function draftChoices(
  poolIds: readonly string[],
  ownedIds: readonly string[],
  seed: number,
  draftIndex: number,
  count = 3,
): FieldMod[] {
  const owned = new Set(ownedIds);
  const pool = poolIds
    .map((id) => fieldModById(id))
    .filter((m): m is FieldMod => m !== undefined && !owned.has(m.id));

  const rng = mulberry32((seed ^ (draftIndex * 0x85ebca6b)) | 0);
  const choices: FieldMod[] = [];
  const remaining = [...pool];
  while (choices.length < count && remaining.length > 0) {
    const idx = pickWeighted(rng, remaining, (m) => RARITY_WEIGHT[m.rarity]);
    choices.push(remaining[idx]!);
    remaining.splice(idx, 1);
  }
  return choices;
}

// Everything the engine needs from a set of drafted mods.
export type RunModifiers = {
  loadout: LoadoutMods;
  squadDamageTakenMult: number;
  squadBonusMembers: number;
  aircraftDamageTakenMult: number;
  bonusFlares: number;
  reinforcementBudgetDelta: number;
};

function applyWeaponEffect(mods: WeaponMods, effects: FieldModEffects): void {
  if (effects.cooldownMult !== undefined) mods.cooldownMult *= effects.cooldownMult;
  if (effects.spreadMult !== undefined) mods.spreadMult *= effects.spreadMult;
  if (effects.blastMult !== undefined) mods.blastRadiusMult *= effects.blastMult;
  if (effects.tracerBright) mods.tracerBright = true;
}

export function computeRunModifiers(modIds: Iterable<string>): RunModifiers {
  const out: RunModifiers = {
    loadout: {
      chaingun: { ...DEFAULT_WEAPON_MODS },
      autocannon: { ...DEFAULT_WEAPON_MODS },
      howitzer: { ...DEFAULT_WEAPON_MODS },
    },
    squadDamageTakenMult: 1,
    squadBonusMembers: 0,
    aircraftDamageTakenMult: 1,
    bonusFlares: 0,
    reinforcementBudgetDelta: 0,
  };

  for (const id of modIds) {
    const mod = fieldModById(id);
    if (!mod) continue;
    const fx = mod.effects;
    if (fx.weapon === "all") {
      applyWeaponEffect(out.loadout.chaingun, fx);
      applyWeaponEffect(out.loadout.autocannon, fx);
      applyWeaponEffect(out.loadout.howitzer, fx);
    } else if (fx.weapon) {
      applyWeaponEffect(out.loadout[fx.weapon], fx);
    }
    if (fx.squadDamageTakenMult !== undefined) out.squadDamageTakenMult *= fx.squadDamageTakenMult;
    if (fx.squadBonusMembers) out.squadBonusMembers += fx.squadBonusMembers;
    if (fx.aircraftDamageTakenMult !== undefined) out.aircraftDamageTakenMult *= fx.aircraftDamageTakenMult;
    if (fx.bonusFlares) out.bonusFlares += fx.bonusFlares;
    if (fx.reinforcementBudgetDelta) out.reinforcementBudgetDelta += fx.reinforcementBudgetDelta;
  }

  return out;
}
